import React, { useState } from 'react';
import './RootBallV2.css';
import { typeFlowerColors } from '../constants/constants';

export function RootBallV2({ tutorData, maxCount = 20 }) {
  const [hoveredBall, setHoveredBall] = useState(null);

  if (!tutorData || !tutorData.processedData) {
    return null;
  }

  const { processedData } = tutorData;  
  // Filter out zero counts and sort by count (descending)
  const activeTalkMoves = Object.entries(processedData)
    .filter(([_, count]) => count > 0);
    //.sort((a, b) => b[1] - a[1]);

  if (activeTalkMoves.length === 0) {
    return null;
  }

  // Calculate sizes - largest gets 100%, others scale proportionally
  const minSize = 35;
  const maxSize = 80; 

  // sizes are based on ranges that are specific to the talk move types
  const getCircleSize = (type, count) => {
    const maxRanges = {
      accuracy: 50,
      reasoning: 15,
      students_relating: 10
    };
    
    const maxRange = maxRanges[type] || 20; // Default to reasoning range
    const clampedCount = Math.min(count, maxRange);
    const scale = clampedCount / maxRange;
    return minSize + (maxSize - minSize) * scale;
  };

  // Get color for talk move type
  const getColor = (type) => {
    const colorClass = typeFlowerColors[type] || 'whiteflower';
    const colorMap = {
      'redflower': '#f07d7f',
      'greenflower': '#aae76d',
      'blueflower': '#79aaee',
      'yellowflower': '#eed571',
      'purpleflower': '#c26be4',
      'blackflower': '#333',
      'whiteflower': '#e0e0e0'
    };
    return colorMap[colorClass] || '#e0e0e0';
  };

  // Format talk move type name for display
  const formatTypeName = (type) => {
    return type
      .split(/(?=[A-Z])/)
      .join(' ')
      .replace(/^./, str => str.toUpperCase());
  };

  // Calculate positions for each ball with wider spacing
  const getPosition = (index, total) => {
    if (total === 1) {
      return { left: '50%', transform: 'translateX(-50%)' };
    } else if (total === 2) {
      // Wider spacing for 2 balls
      return index === 0 
        ? { left: '25%', transform: 'translateX(-50%)' }
        : { left: '75%', transform: 'translateX(-50%)' };
    } else if (total === 3) {
      // Evenly spaced for 3 balls with good spacing
      const positions = ['20%', '50%', '80%'];
      return { left: positions[index], transform: 'translateX(-50%)' };
    } else {
      // For 4+ balls, spread evenly across the width
      const spacing = 70 / (total - 1); // Use 70% of width
      const leftPercent = 15 + spacing * index;
      return { left: `${leftPercent}%`, transform: 'translateX(-50%)' };
    }
  };

  return (
    <div className="root-ball-container" role="img" aria-label="Tutor talk moves visualization">
      {/* Root connections - Smooth curves matching reference SVG design */}
      <svg className="root-connections" viewBox="0 0 400 200" preserveAspectRatio="none">
        {activeTalkMoves.map(([type, count], index) => {
          const total = activeTalkMoves.length;
          const position = getPosition(index, total);
          const leftPercent = parseFloat(position.left);
          
          // Start from tree trunk base (center)
          const startX = 200;
          const startY = 0;
          const endX = (leftPercent / 100) * 400;
          const endY = 180;
          
          // Determine curve direction based on position
          let curveDirection = 0;
          
          if (total === 1) {
            curveDirection = 0; // Center
          } else if (total === 2) {
            curveDirection = index === 0 ? -1 : 1;
          } else {
            const middleIndex = Math.floor(total / 2);
            if (index < middleIndex) {
              curveDirection = -1; // Left
            } else if (index > middleIndex) {
              curveDirection = 1; // Right
            } else {
              curveDirection = 0; // Middle
            }
          }
          
          let mainPath;
          
          if (curveDirection === 0) {
            // Middle root - straight down
            mainPath = `M ${startX} ${startY} 
                       L ${startX} ${endY}`;
          } else {
            
            const horizontalDistance = Math.abs(endX - startX);
            
           
            const dipPointX = startX + (curveDirection * horizontalDistance * 0.25);
            const dipPointY = endY * 0.3;
          
            
            // Control points for pronounced dip down
            const ctrl1X = startX + (curveDirection * horizontalDistance * 0.05);
            const ctrl1Y = endY * 0.1;
            const ctrl2X = startX + (curveDirection * horizontalDistance * 0.2);
            const ctrl2Y = endY * 0.28;
            
            // Control points for the plateau and rise
            const ctrl3X = startX + (curveDirection * horizontalDistance * 0.5);
            const ctrl3Y = endY * 0.33;
            const ctrl4X = endX - (curveDirection * 15);
            const ctrl4Y = endY * 0.5;
            
            // Create pronounced S-curve: dip → plateau → rise
            mainPath = `M ${startX} ${startY} 
                       C ${ctrl1X} ${ctrl1Y}, ${ctrl2X} ${ctrl2Y}, ${dipPointX} ${dipPointY}
                       C ${ctrl3X} ${ctrl3Y}, ${ctrl4X} ${ctrl4Y}, ${endX} ${endY}`;
          }
          
          const branches = [];
          
          return (
            <g key={`root-group-${type}`}>
              {/* Main root */}
              <path
                d={mainPath}
                className="root-path root-path-main"
                style={{
                  animationDelay: `${index * 0.2}s`,
                  strokeWidth: 4 + (count / Math.max(50, 20, 10)) * 3
                }}
              />
              
              {/* Branch roots */}
              {branches.map((branch, bIndex) => (
                <path
                  key={`branch-${type}-${bIndex}`}
                  d={branch.path}
                  className="root-path root-path-branch"
                  style={{
                    animationDelay: `${index * 0.2 + (bIndex + 1) * 0.15}s`,
                    strokeWidth: 2 + Math.abs(Math.sin(branch.seed)) * 1.5
                  }}
                />
              ))}
            </g>
          );
        })}
      </svg>

      {/* Root balls */}
      <div className="root-ball-circles">
        {activeTalkMoves.map(([type, count], index) => {
          const size = getCircleSize(type, count);
          const color = getColor(type);
          const position = getPosition(index, activeTalkMoves.length);
          
          return (
            <div
              key={type}
              className="root-ball-circle-wrapper"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                ...position,
                animationDelay: `${index * 0.2}s`
              }}
              onMouseEnter={() => setHoveredBall(index)}
              onMouseLeave={() => setHoveredBall(null)}
            >
              <div
                className="root-ball-circle"
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: color,
                  border: color === '#e0e0e0' ? '2px solid #ccc' : 'none',
                }}
              >
                {/* Tooltip */}
                {hoveredBall === index && (
                  <div className="root-ball-tooltip">
                    <strong>{formatTypeName(type)}</strong>
                    <span>{count}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RootBallV2;
